import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getEffectiveTenantScope } from '../services/patientService';
import {
  ACTIVE_BATCH_STATUSES,
  DOWNLOADABLE_BATCH_STATUSES,
  FINISHED_BATCH_STATUSES,
  downloadPatientReportBatch,
  isTenantWideReportRole,
  subscribeRecentPatientReportBatches,
  timestampMs,
} from '../services/patientReportBatchService';
import '../pages/PatientReportBatch.css';

const BATCH_PATH = '/reports/patient-batch';
const REPORT_ROLES = new Set(['admin', 'clinical admin', 'superuser', 'supervisor', 'manager', 'care manager']);
const TOAST_MS = 12000;

const PatientReportBatchNotifyContext = createContext({ unseenCount: 0 });

function seenKey(uid, tenantId) {
  return `t1.patientBatchSeen.${uid}.${tenantId}`;
}

function readSeen(uid, tenantId) {
  try {
    const raw = localStorage.getItem(seenKey(uid, tenantId));
    if (raw == null) {
      const now = Date.now();
      localStorage.setItem(seenKey(uid, tenantId), String(now));
      return now;
    }
    return Number(raw) || 0;
  } catch {
    return Date.now();
  }
}

function writeSeen(uid, tenantId, ms) {
  try {
    localStorage.setItem(seenKey(uid, tenantId), String(ms));
  } catch {
    /* ignore unavailable storage */
  }
}

/** Runs the user should hear about: their own, plus month-end runs for tenant-wide roles. */
function isRelevant(run, uid, tenantWide) {
  return run.createdByUid === uid || (tenantWide && run.kind === 'month_end');
}

function finishedMs(run) {
  return timestampMs(run.completedAt) || timestampMs(run.updatedAt);
}

function toastText(run) {
  const label = run.periodLabel || (run.ymKeys || []).join(', ');
  const prefix = run.kind === 'month_end' ? 'Month-end patient summaries' : 'Patient report batch';
  if (run.status === 'failed') return `${prefix} for ${label} failed.`;
  const failed = run.counts?.failed || 0;
  return `${prefix} for ${label} ${failed ? `finished with ${failed} error${failed === 1 ? '' : 's'}` : 'is ready'}.`;
}

export function PatientReportBatchNotifyProvider({ children }) {
  const { user, profile } = useAuth();
  const location = useLocation();
  const uid = user?.uid || '';
  const role = String(profile?.role || '').toLowerCase().trim();
  const tenantWide = isTenantWideReportRole(role);
  const tenantScope = useMemo(() => (profile ? getEffectiveTenantScope(profile) : null), [profile]);
  const tenantId = tenantScope?.mode === 'tenant' ? tenantScope.tenantId : '';
  const enabled = !!uid && !!tenantId && REPORT_ROLES.has(role);
  const onBatchPage = location.pathname === BATCH_PATH;

  const [runs, setRuns] = useState([]);
  const [lastSeenMs, setLastSeenMs] = useState(0);
  const [toast, setToast] = useState(null);
  const statusById = useRef(new Map());

  useEffect(() => {
    statusById.current = new Map();
    setRuns([]);
    setToast(null);
    if (!enabled) return undefined;
    setLastSeenMs(readSeen(uid, tenantId));
    return subscribeRecentPatientReportBatches(
      { tenantId, uid, tenantWide, max: 15 },
      (rows) => setRuns(rows),
      () => setRuns([]),
    );
  }, [enabled, uid, tenantId, tenantWide]);

  // Toast when a run seen in progress during this session finishes.
  useEffect(() => {
    const prev = statusById.current;
    const next = new Map();
    runs.forEach((run) => {
      next.set(run.id, run.status);
      const before = prev.get(run.id);
      if (
        before
        && ACTIVE_BATCH_STATUSES.has(before)
        && FINISHED_BATCH_STATUSES.has(run.status)
        && isRelevant(run, uid, tenantWide)
        && !onBatchPage
      ) {
        setToast({
          id: run.id,
          text: toastText(run),
          failed: run.status === 'failed',
          downloadable: DOWNLOADABLE_BATCH_STATUSES.has(run.status) && !!run.zip,
        });
      }
    });
    statusById.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const markSeen = useCallback(() => {
    if (!enabled) return;
    const now = Date.now();
    writeSeen(uid, tenantId, now);
    setLastSeenMs(now);
  }, [enabled, uid, tenantId]);

  // Visiting the batch page clears the badge (and keeps it clear while open).
  useEffect(() => {
    if (onBatchPage) {
      markSeen();
      setToast(null);
    }
  }, [onBatchPage, runs, markSeen]);

  const unseenCount = useMemo(
    () =>
      runs.filter(
        (run) =>
          FINISHED_BATCH_STATUSES.has(run.status)
          && isRelevant(run, uid, tenantWide)
          && finishedMs(run) > lastSeenMs,
      ).length,
    [runs, uid, tenantWide, lastSeenMs],
  );

  const value = useMemo(() => ({ unseenCount, markSeen }), [unseenCount, markSeen]);

  return (
    <PatientReportBatchNotifyContext.Provider value={value}>
      {children}
      {toast ? (
        <div className={`patient-batch-toast${toast.failed ? ' patient-batch-toast--failed' : ''}`} role="status">
          <span>{toast.text}</span>
          {toast.downloadable ? (
            <button
              type="button"
              className="patient-batch-toast-action"
              onClick={() => {
                const id = toast.id;
                setToast(null);
                downloadPatientReportBatch(id).catch(() => {});
              }}
            >
              Download
            </button>
          ) : null}
          <Link to={BATCH_PATH} onClick={() => setToast(null)}>
            View runs
          </Link>
          <button type="button" className="patient-batch-toast-close" aria-label="Dismiss" onClick={() => setToast(null)}>
            ×
          </button>
        </div>
      ) : null}
    </PatientReportBatchNotifyContext.Provider>
  );
}

export function usePatientReportBatchNotify() {
  return useContext(PatientReportBatchNotifyContext);
}
